export type SocialProvider = 'youtube' | 'instagram' | 'tiktok' | 'x' | 'facebook' | 'link';

export interface LinkPreviewData {
  url: string;
  provider: SocialProvider;
  title?: string;
  description?: string;
  thumbnail_url?: string;
  author_name?: string;
}

const TRAILING_URL_PUNCTUATION = /[.,;:!?\])}]+$/;
const HTTP_URL_PATTERN = /^https?:\/\/([^/?#\s]+)(?:[/?#]|$)/iu;

const isDomain = (host: string, domain: string) => host === domain || host.endsWith(`.${domain}`);

const hostForUrl = (url: string): string | null => {
  const match = url.match(HTTP_URL_PATTERN);
  if (!match?.[1]) return null;

  const authority = match[1].split('@').pop() ?? '';
  const host = authority.startsWith('[')
    ? authority.slice(1, authority.indexOf(']'))
    : authority.split(':')[0];

  return host ? host.toLowerCase().replace(/^www\./, '') : null;
};

export const extractFirstHttpUrl = (text?: string | null): string | null => {
  const match = text?.match(/https?:\/\/[^\s<]+/iu);
  if (!match) return null;

  const url = match[0].replace(TRAILING_URL_PUNCTUATION, '');
  return hostForUrl(url) ? url : null;
};

export const previewHost = (url: string): string => hostForUrl(url) ?? url;

export const providerForUrl = (url: string): SocialProvider => {
  const host = hostForUrl(url);
  if (!host) return 'link';

  if (host === 'youtu.be' || isDomain(host, 'youtube.com')) return 'youtube';
  if (isDomain(host, 'instagram.com')) return 'instagram';
  if (isDomain(host, 'tiktok.com')) return 'tiktok';
  if (isDomain(host, 'x.com') || isDomain(host, 'twitter.com')) return 'x';
  if (isDomain(host, 'facebook.com') || host === 'fb.watch') return 'facebook';

  return 'link';
};

export const fallbackLinkPreview = (text?: string | null): LinkPreviewData | null => {
  const url = extractFirstHttpUrl(text);
  if (!url) return null;

  return { url, provider: providerForUrl(url) };
};

export const providerLabel = (provider: SocialProvider) => ({
  youtube: 'YouTube',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  x: 'X',
  facebook: 'Facebook',
  link: 'Link',
}[provider]);
